
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to migrate from Plaid\'s legacy categories to the new personal finance categories'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Personal Finance Category migration guide' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "migrating-from-personal-finance-categories-v1-to-pfcv2"
    }}>{`Migrating from Personal Finance Categories v1 to PFCv2`}</h4>
    <h5 {...{
      "id": "whats-in-pfcv2"
    }}>{`What's in PFCv2`}</h5>
    <p>{`In December 2025, Plaid released a new version of Personal Finance Categories, called PFCv2, including both improved transaction categorization accuracy and a new, more granular transaction schema with additional categories specifically useful for Earned Wage Access (EWA) use cases, with six additional income subcategories, six additional loan disbursement subcategories, and three additional loan repayment subcategories. Additional subcategories have also been added for bank fees and for transfers, including wires and crypto. `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/pfc-taxonomy-all.csv"
      }}>{`See a full comparison of PFCv2 and PFCv1`}</a>{`. `}</p>
    <h5 {...{
      "id": "how-to-migrate"
    }}>{`How to migrate`}</h5>
    <p>{`Opting in to PFCv2 is required for existing customers to receive the new, more granular subcategories. All customers, regardless of whether they opt in to PFCv2, will automatically receive the accuracy improvements, with the exception of a small subset of customers in the EWA industry, who will not be automatically opted in to receive the accuracy improvements until 2027 unless they opt in to PFCv2. All new customers enabled for Transactions after December 3, 2025 will be opted in to PFCv2 by default. `}</p>
    <p>{`To opt in to PFCv2, existing customers should set `}<inlineCode parentName="p">{`options.personal_finance_category_version`}</inlineCode>{` to `}<inlineCode parentName="p">{`v2`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transactions/enrich`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/transactions/recurring/get`}</inlineCode>{` request. `}</p>
    <p>{`Before migrating, make sure your app can accept the new categories and subcategories used by PFCv2. The work involved will depend on your integration; for some customers, this may not require any action at all. If you maintain logic that maps PFCs to your own internal categories, you will need to update it to incorporate the new values. See the `}<a parentName="p" {...{
        "href": "[redacted]#############################################################################################################################"
      }}>{`PFCv2 to PFCv1 mapping specification`}</a>{`. You can also download the CSV `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/pfc-taxonomy-all.csv"
      }}>{`here`}</a>{`.`}</p>
    <h5 {...{
      "id": "backfilling-historical-transactions-with-pfcv2"
    }}>{`Backfilling historical transactions with PFCv2`}</h5>
    <p>{`To apply the PFCv2 categories to existing transactions that you already fetched or that were already extracted prior to the migration, you can re-call any of the endpoints while passing in the `}<inlineCode parentName="p">{`options.personal_finance_category_version: "v2"`}</inlineCode>{` parameter. The improvements to accuracy and confidence will not be backfilled to these pre-existing transactions; only the new taxonomy will be applied.`}</p>
    <h4 {...{
      "id": "migrating-from-legacy-categories-to-personal-finance-categories"
    }}>{`Migrating from legacy categories to Personal Finance Categories`}</h4>
    <Callout mdxType="Callout">
      <p>{`This section is only relevant to customers who are still using Plaid's legacy `}<inlineCode parentName="p">{`category`}</inlineCode>{` system, which was superseded by Personal Finance Categories in September 2023. Customers who migrate will receive PFCv1 by default and must set `}<inlineCode parentName="p">{`options.personal_finance_category_version`}</inlineCode>{` to `}<inlineCode parentName="p">{`v2`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transactions/enrich`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/transactions/recurring/get`}</inlineCode>{` request to migrate directly to the latest Personal Finance Categories, PFCv2. `}</p>
    </Callout>
    <p>{`Personal finance categories (PFCs), returned by all Transactions endpoints as of September 2023, provide more meaningful categorization at greater accuracy compared to the legacy category fields. All Transactions implementations are recommended to use PFCs rather than the legacy taxonomy. The PFC taxonomy is composed of fewer, better-defined categories based on customer needs and what users expect to see in a personal finance management app. Although PFCs are most relevant for personal finance use cases, they are not limited to those use cases.`}</p>
    <p>{`PFCs are composed of the following fields:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`personal_finance_category.primary`}</inlineCode>{`: A high level category that communicates the broad category of the transaction`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`personal_finance_category.detailed`}</inlineCode>{`: A granular category conveying the transaction's intent; this field can also be used as a unique identifier for the category`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`personal_finance_category.confidence_level`}</inlineCode>{`: A description of how confident we are that the provided categories accurately describe the transaction's intent`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`personal_finance_category_icon_url`}</inlineCode>{`: The URL of an icon associated with the primary personal finance category`}</li>
    </ul>
    <p>{`See an example of these two types of categorization below:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Legacy category:",
        "switcher": "false",
        "#Legacy": true,
        "category:": true
      }}>{`...
"category": [
  "Shops",
  "Computers and Electronics"
],
"category_id": "19013000",
...
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Personal finance category:",
        "switcher": "false",
        "#Personal": true,
        "finance": true,
        "category:": true
      }}>{`...
"personal_finance_category": {
  "primary": "GENERAL_MERCHANDISE",
  "detailed": "GENERAL_MERCHANDISE_ELECTRONICS",
  "confidence_level": "VERY_HIGH"
},
"personal_finance_category_icon_url": "https://plaid-category-icons.plaid.com/PFC_GENERAL_MERCHANDISE.png",
...
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/pfc-taxonomy-all.csv"
      }}>{`taxonomy CSV file`}</a>{` for a full list of PFCs.`}</p>
    <p>{`Plaid has no plans to remove the legacy categories from the API response at this time. However, we are no longer developing improvements to those fields and strongly recommend using PFCs for the most accurate and up-to-date categorization.`}</p>
    <p>{`Note that PFCs are not present in Assets endpoints.`}</p>
    <h5 {...{
      "id": "updating-your-internal-categorization-logic"
    }}>{`Updating your internal categorization logic`}</h5>
    <p>{`Some customers maintain logic that translates Plaid's legacy categories into their own internal categories. In order to facilitate the updating of this translation layer, see the `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/transactions-personal-finance-category-mapping.json"
      }}>{`mapping JSON file`}</a>{` for a suggested 1-to-many mapping of our most common legacy categories to possible PFCs. Please note that this file is intended for general taxonomy-to-taxonomy mapping, and individual transactions may be assigned a PFC unaligned with its legacy category as we continue to refine the accuracy of our PFC predictions.`}</p>
    <h5 {...{
      "id": "backfilling-historical-transactions-with-pfcs"
    }}>{`Backfilling historical transactions with PFCs`}</h5>
    <p>{`You may already have historical transactions stored with only the legacy category present. If you are interested in backfilling PFCs for those transactions, you can re-fetch those transactions with a populated PFC using the `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` endpoints.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "migrating-from-personal-finance-categories-v1-to-pfcv2",
        level: 4,
        title: "Migrating from Personal Finance Categories v1 to PFCv2",
      },
{
        id: "migrating-from-legacy-categories-to-personal-finance-categories",
        level: 4,
        title: "Migrating from legacy categories to Personal Finance Categories",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
